import { useMemo } from "react";
import { format, addDays, startOfWeek, isSameDay } from "date-fns";
import { it } from "date-fns/locale";

/**
 * AgendaSettimanaleView — Vista compatta Lun-Sab con appuntamenti come mini card,
 * orari sticky sulla sinistra. Read-only per navigazione veloce.
 *
 * Props:
 *   dataCorrente: Date — data di riferimento (mostra la settimana che la contiene)
 *   appuntamenti: array — appuntamenti della settimana (già filtrati/caricati per la settimana)
 *   operatori: array
 *   servizi: array
 *   onSelectDay: (Date) => void  — click su header giorno → torna a vista giornaliera
 *   onSelectApp: (app) => void   — click su card → apre quick-edit
 *   oraApertura: number (es. 9)
 *   oraChiusura: number (es. 20)
 */
export default function AgendaSettimanaleView({
  dataCorrente,
  appuntamenti = [],
  operatori = [],
  servizi = [],
  onSelectDay,
  onSelectApp,
  oraApertura = 8,
  oraChiusura = 20,
}) {
  const giorni = useMemo(() => {
    const inizio = startOfWeek(dataCorrente, { weekStartsOn: 1 }); // lunedì
    return Array.from({ length: 6 }).map((_, i) => addDays(inizio, i)); // Lun-Sab
  }, [dataCorrente]);

  const oreLabel = useMemo(() => {
    const arr = [];
    for (let h = oraApertura; h <= oraChiusura; h++) arr.push(h);
    return arr;
  }, [oraApertura, oraChiusura]);

  const operatoreById = useMemo(() => {
    const m = {};
    operatori.forEach(o => { m[o.id] = o; });
    return m;
  }, [operatori]);

  const servizioById = useMemo(() => {
    const m = {};
    servizi.forEach(s => { m[s.id] = s; });
    return m;
  }, [servizi]);

  const appsByDay = useMemo(() => {
    const map = {};
    giorni.forEach(g => { map[format(g, 'yyyy-MM-dd')] = []; });
    appuntamenti.forEach(a => {
      const d = a.data;
      if (d && map[d] !== undefined) map[d].push(a);
    });
    // ordina per ora
    Object.values(map).forEach(arr => arr.sort((x, y) => (x.ora || '').localeCompare(y.ora || '')));
    return map;
  }, [appuntamenti, giorni]);

  const today = new Date();

  return (
    <div className="border rounded-2xl overflow-hidden bg-white shadow-sm" data-testid="vista-settimanale">
      {/* Header giorni */}
      <div
        className="grid sticky top-0 z-30 bg-white border-b"
        style={{ gridTemplateColumns: `50px repeat(6, 1fr)` }}
      >
        <div className="border-r bg-slate-50" />
        {giorni.map((g) => {
          const isToday = isSameDay(g, today);
          const dayApps = appsByDay[format(g, 'yyyy-MM-dd')] || [];
          return (
            <button
              key={format(g, 'yyyy-MM-dd')}
              onClick={() => onSelectDay?.(g)}
              className={`py-2 px-1 border-r last:border-r-0 text-center hover:bg-blue-50 transition ${
                isToday ? 'bg-blue-50' : ''
              }`}
              data-testid={`settimana-day-${format(g, 'yyyy-MM-dd')}`}
              title="Apri vista giornaliera"
            >
              <p className={`text-[10px] uppercase tracking-wider font-semibold ${isToday ? 'text-blue-600' : 'text-slate-500'}`}>
                {format(g, 'EEE', { locale: it })}
              </p>
              <p className={`text-lg font-black ${isToday ? 'text-blue-700' : 'text-slate-800'}`}>
                {format(g, 'd')}
              </p>
              <p className="text-[9px] text-slate-500 -mt-0.5">
                {format(g, 'MMM', { locale: it })}
              </p>
              {dayApps.length > 0 && (
                <span className="inline-block mt-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                  {dayApps.length} app
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Grid orari x giorni */}
      <div className="relative">
        <div
          className="grid"
          style={{ gridTemplateColumns: `50px repeat(6, 1fr)` }}
        >
          {/* Colonna orari (sticky sinistra) */}
          <div className="sticky left-0 z-20 bg-slate-50 border-r">
            {oreLabel.map(h => (
              <div key={h} className="h-16 flex items-start justify-center pt-1 text-[10px] font-semibold text-slate-500 border-b">
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Colonne giorni */}
          {giorni.map((g) => {
            const ymd = format(g, 'yyyy-MM-dd');
            const dayApps = appsByDay[ymd] || [];
            return (
              <div key={ymd} className="border-r last:border-r-0 relative" style={{ minHeight: `${(oreLabel.length) * 64}px` }}>
                {/* Righe orari (background) */}
                {oreLabel.map(h => (
                  <div key={h} className="h-16 border-b border-slate-100 hover:bg-slate-50/50" />
                ))}

                {/* Appuntamenti come card posizionati assolutamente */}
                {dayApps.map((app) => {
                  const ora = app.ora || '00:00';
                  const [hh, mm] = ora.split(':').map(Number);
                  const startMin = hh * 60 + mm;
                  const openMin = oraApertura * 60;
                  const durata = app.durata_totale || 30;
                  const top = ((startMin - openMin) / 60) * 64;
                  const height = Math.max((durata / 60) * 64, 20);
                  const op = operatoreById[app.operatore_id];
                  const primoServ = app.servizi?.[0];
                  const servRef = primoServ ? servizioById[primoServ.servizio_id] : null;
                  const bg = op?.colore || '#6366f1';
                  if (top < 0 || top > oreLabel.length * 64) return null;
                  return (
                    <button
                      key={app.id}
                      onClick={() => onSelectApp?.(app)}
                      className="absolute left-0.5 right-0.5 rounded-md text-white overflow-hidden text-left px-1 py-0.5 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all z-10 border border-white/40"
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                        background: `linear-gradient(135deg, ${bg}, ${bg}cc)`,
                      }}
                      data-testid={`settimana-app-${app.id}`}
                      title={`${app.cliente_nome} • ${primoServ?.nome || ''} (${ora})`}
                    >
                      <p className="text-[9px] font-bold leading-tight line-clamp-2" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.6)' }}>
                        {app.cliente_nome || 'Cliente'}
                      </p>
                      {height >= 32 && (
                        <p className="text-[8px] opacity-90 truncate" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.5)' }}>
                          {ora} · {servRef?.nome || primoServ?.nome || ''}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
